import { getCurrentLocalTimestamp, localNowDateString } from '@/app/utils/localDate';
// ============================================
// 🎯 EXPENSE CONTEXT
// ============================================
// Manages expenses with auto-numbering and accounting integration

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { useAccountingOptional } from '@/app/context/AccountingContext';
import { useSupabase } from '@/app/context/SupabaseContext';
import { expenseService, Expense as SupabaseExpense } from '@/app/services/expenseService';
import { syncExpenseLinkedPayment } from '@/app/services/expensePaymentSyncService';
import { accountingService } from '@/app/services/accountingService';
import { voidJournalEntries } from '@/app/services/accountingIntegrityService';
import {
  assertDomainEditSafetyTestMode,
  buildExpenseCanonicalComparisonRows,
  classifyPaidExpenseEdit,
  type ExpenseEditClassification,
  type PaidExpenseSnapshot,
} from '@/app/lib/accountingEditClassification';
import { normalizeNullableText } from '@/app/lib/expenseEditCanonical';
import { formatFieldChangeLines, logDocumentEditActivity } from '@/app/lib/documentEditActivityLog';
import {
  createExpenseEditTraceId,
  pushExpenseEditTrace,
} from '@/app/lib/expenseEditTrace';
import { supabase } from '@/lib/supabase';
import { fetchInBatches } from '@/app/lib/chunkInQuery';
import { formatPaymentAccountLabel } from '@/app/lib/paymentAccountDisplay';
import {
  isLiquidityPaymentAccount,
  postedPaymentAccountIdFromRow,
  postedPaymentDisplayFromRow,
} from '@/app/lib/resolveExpensePaymentAccount';
import { toast } from 'sonner';
import { isPostedExpenseStatus } from '@/app/lib/expenseCancelPolicy';
import {
  DATA_INVALIDATED_EVENT,
  dispatchAccountingInvalidated,
  dispatchDataInvalidated,
  dispatchExpenseLifecycleInvalidated,
  shouldAcceptInvalidation,
  type DataInvalidationDetail,
} from '@/app/lib/dataInvalidationBus';

// ============================================
// TYPES
// ============================================

export type ExpenseStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
export type ExpenseCategory = 
  | 'Rent' 
  | 'Utilities' 
  | 'Salaries' 
  | 'Marketing' 
  | 'Travel' 
  | 'Office Supplies' 
  | 'Repairs & Maintenance'
  | 'Other';

export interface Expense {
  id: string;
  expenseNo: string;
  category: ExpenseCategory | string;
  /** FK to expense_categories when used */
  expense_category_id?: string;
  description: string;
  amount: number;
  date: string;
  paymentMethod: string;
  /** Human-readable payment account (code — name) for display */
  paymentAccountDisplay?: string;
  /** Chart payment account UUID when stored on the expense row */
  paymentAccountId?: string;
  payeeName: string;
  paidToUserId?: string;
  location: string;
  status: ExpenseStatus;
  submittedBy: string;
  approvedBy?: string;
  approvedDate?: string;
  receiptAttached: boolean;
  receiptUrl?: string;
  /** Linked payments.reference_number when reference_type=expense (may equal expenseNo after unify). */
  paymentReference?: string | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface ExpenseContextType {
  expenses: Expense[];
  loading: boolean;
  getExpenseById: (id: string) => Expense | undefined;
  createExpense: (expense: Omit<Expense, 'id' | 'expenseNo' | 'createdAt' | 'updatedAt'> & { category: ExpenseCategory | string }, options?: { branchId?: string; payment_account_id?: string; paidToUserId?: string; expense_category_id?: string }) => Promise<Expense>;
  updateExpense: (id: string, updates: Partial<Expense>, options?: { silent?: boolean }) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  cancelExpense: (id: string, reason?: string) => Promise<void>;
  approveExpense: (id: string, approvedBy: string) => Promise<void>;
  rejectExpense: (id: string) => Promise<void>;
  markAsPaid: (id: string, paymentMethod: string) => Promise<void>;
  getExpensesByCategory: (category: ExpenseCategory) => Expense[];
  getExpensesByStatus: (status: ExpenseStatus) => Expense[];
  getTotalByCategory: (category: ExpenseCategory) => number;
  refreshExpenses: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

export const useExpenses = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    // During hot reload in development, context might not be available
    // During hot reload or initial mount, context might not be available; return safe default to prevent crashes
    if (import.meta.env.DEV) {
      const defaultError = () => { throw new Error('ExpenseProvider not available'); };
      return {
        expenses: [],
        loading: false,
        getExpenseById: () => undefined,
        createExpense: defaultError,
        updateExpense: async () => {
          defaultError();
        },
        deleteExpense: defaultError,
        cancelExpense: defaultError,
        recordPayment: defaultError,
        getExpensesByDateRange: () => [],
        getTotalByCategory: () => 0,
        refreshExpenses: async () => {},
      } as ExpenseContextType;
    }
    throw new Error('useExpenses must be used within ExpenseProvider');
  }
  const activate = (context as ExpenseContextType & { __activate?: () => void }).__activate;
  useEffect(() => {
    activate?.();
  }, [activate]);
  return context;
};

// ============================================
// PROVIDER
// ============================================

export const ExpenseProvider = ({ children }: { children: ReactNode }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const accounting = useAccountingOptional();
  const { companyId, branchId, user, requiresBranchSelection } = useSupabase();
  /** Serialize updates per expense so two saves cannot both reverse+repost the same JE. */
  const expenseUpdateChainRef = useRef<Map<string, Promise<unknown>>>(new Map());

  // Map category from app format to Supabase format (accepts slug string from expense_categories)
  const mapCategoryToSupabase = (category: ExpenseCategory | string): string => {
    if (typeof category === 'string' && /^[a-z0-9_]+$/.test(category)) return category;
    const mapping: Record<ExpenseCategory, string> = {
      'Rent': 'rent',
      'Utilities': 'utilities',
      'Salaries': 'salaries',
      'Marketing': 'marketing',
      'Travel': 'travel',
      'Office Supplies': 'office_supplies',
      'Repairs & Maintenance': 'repairs',
      'Other': 'miscellaneous',
    };
    return mapping[category as ExpenseCategory] || (typeof category === 'string' ? category.toLowerCase().replace(/\s+/g, '_') : 'miscellaneous');
  };

  // Map category from Supabase format to app format (slug, name, or display name)
  const mapCategoryFromSupabase = (category: string | null | undefined): ExpenseCategory | string => {
    const mapping: Record<string, ExpenseCategory> = {
      'rent': 'Rent', 'utilities': 'Utilities', 'salaries': 'Salaries', 'marketing': 'Marketing',
      'travel': 'Travel', 'office_supplies': 'Office Supplies', 'repairs': 'Repairs & Maintenance',
      'professional_fees': 'Other', 'insurance': 'Other', 'taxes': 'Other', 'miscellaneous': 'Other',
    };
    if (!category) return 'Other';
    const slug = category.toLowerCase().trim().replace(/\s+/g, '_');
    if (mapping[slug]) return mapping[slug];
    if (mapping[category]) return mapping[category];
    return category; // Already display name from expense_categories.name
  };

  // Convert Supabase expense format to app format
  const convertFromSupabaseExpense = useCallback((supabaseExpense: any): Expense => {
    const id = supabaseExpense.id || '';
    const expenseNo = supabaseExpense.expense_no || '';
    // Use expense_category join when category string is empty (expense_category_id used)
    const categoryRaw = supabaseExpense.category
      || supabaseExpense.expense_category?.name
      || supabaseExpense.expense_category?.slug;
    const paymentAccountFromJoin = supabaseExpense.payment_account;
    const paymentAccountFromContext =
      supabaseExpense.payment_account_id && accounting?.accounts
        ? accounting.accounts.find((a) => a.id === supabaseExpense.payment_account_id)
        : undefined;
    const postedAccount = supabaseExpense.posted_payment_account as
      | { code?: string; name?: string; type?: string }
      | null
      | undefined;
    const postedAccountId =
      supabaseExpense.posted_payment_account_id as string | undefined;
    const usePosted =
      postedAccountId &&
      (postedAccount ? isLiquidityPaymentAccount(postedAccount) : true);
    const paymentAccountDisplay = usePosted
      ? formatPaymentAccountLabel({
          paymentAccount: postedAccount || null,
          paymentMethod: supabaseExpense.payment_method,
        })
      : postedPaymentDisplayFromRow(supabaseExpense);
    const resolvedPaymentAccountId =
      postedPaymentAccountIdFromRow(supabaseExpense) ||
      (usePosted ? postedAccountId : undefined) ||
      (paymentAccountFromJoin && isLiquidityPaymentAccount(paymentAccountFromJoin)
        ? supabaseExpense.payment_account_id
        : paymentAccountFromContext && isLiquidityPaymentAccount(paymentAccountFromContext)
          ? supabaseExpense.payment_account_id
          : undefined);
    const receiptUrl = supabaseExpense.receipt_url || undefined;
    return {
      id,
      expenseNo,
      category: mapCategoryFromSupabase(categoryRaw),
      expense_category_id: supabaseExpense.expense_category_id || undefined,
      description: supabaseExpense.description || '',
      amount: supabaseExpense.amount || 0,
      date: supabaseExpense.expense_date || localNowDateString(),
      paymentMethod: supabaseExpense.payment_method || 'Cash',
      paymentAccountDisplay,
      paymentAccountId: resolvedPaymentAccountId || undefined,
      payeeName:
        supabaseExpense.vendor_name ||
        supabaseExpense.supplier_name ||
        supabaseExpense.payee_name ||
        '',
      paidToUserId: supabaseExpense.paid_to_user_id || undefined,
      location: supabaseExpense.branch_id || '',
      status: supabaseExpense.status || 'draft',
      submittedBy: supabaseExpense.created_by_user?.full_name || supabaseExpense.created_by || '',
      approvedBy: supabaseExpense.approved_by_user?.full_name || supabaseExpense.approved_by,
      approvedDate: supabaseExpense.approved_at,
      receiptAttached: Boolean(receiptUrl),
      receiptUrl,
      paymentReference:
        supabaseExpense.payment_reference_number != null &&
        String(supabaseExpense.payment_reference_number).trim() !== ''
          ? String(supabaseExpense.payment_reference_number).trim()
          : undefined,
      notes: supabaseExpense.notes,
      createdAt: supabaseExpense.created_at || getCurrentLocalTimestamp(),
      updatedAt: supabaseExpense.updated_at || getCurrentLocalTimestamp(),
    };
  }, [accounting?.accounts]);

  // Load expenses from database
  const loadExpenses = useCallback(async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const data = await expenseService.getAllExpenses(companyId, branchId === 'all' ? undefined : branchId || undefined);
      const mapped = data.map(convertFromSupabaseExpense);
      const ids = mapped.map((e) => e.id).filter(Boolean);
      if (ids.length > 0) {
        const payRows = await fetchInBatches(
          ids,
          async (chunk) => {
            const { data, error } = await supabase
              .from('payments')
              .select('reference_id, reference_number, created_at')
              .eq('company_id', companyId)
              .eq('reference_type', 'expense')
              .in('reference_id', chunk)
              .is('voided_at', null)
              .order('created_at', { ascending: false });
            if (error) throw error;
            return (data || []) as Array<{
              reference_id?: string | null;
              reference_number?: string | null;
            }>;
          },
          { chunkSize: 40 },
        );
        const refByExpenseId = new Map<string, string>();
        for (const row of payRows) {
          const rid = row.reference_id != null ? String(row.reference_id) : '';
          const ref = row.reference_number != null ? String(row.reference_number).trim() : '';
          if (!rid || !ref || refByExpenseId.has(rid)) continue;
          refByExpenseId.set(rid, ref);
        }
        for (const e of mapped) {
          const ref = refByExpenseId.get(e.id);
          if (ref) e.paymentReference = ref;
        }
      }
      setExpenses(mapped);
    } catch (error) {
      console.error('[EXPENSE CONTEXT] Error loading expenses:', error);
      toast.error('Failed to load expenses');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, convertFromSupabaseExpense]);

  const activatedRef = React.useRef(false);
  const [activated, setActivated] = React.useState(false);
  const activate = useCallback(() => {
    if (!activatedRef.current) { activatedRef.current = true; setActivated(true); }
  }, []);

  useEffect(() => {
    if (companyId && activated) loadExpenses();
    else if (!companyId) setLoading(false);
  }, [companyId, activated, loadExpenses]);

  const patchExpenseInList = useCallback(
    async (expenseId: string) => {
      if (!companyId) return;
      try {
        const row = await expenseService.getExpense(expenseId);
        if (!row) return;
        const updated = convertFromSupabaseExpense(row);
        setExpenses((prev) => {
          const idx = prev.findIndex((e) => e.id === expenseId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = updated;
            return next;
          }
          return [updated, ...prev];
        });
      } catch (e) {
        console.warn('[EXPENSE CONTEXT] patchExpenseInList failed:', e);
      }
    },
    [companyId, convertFromSupabaseExpense]
  );

  useEffect(() => {
    if (!companyId || !activated) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const queue = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        void loadExpenses();
      }, 220);
    };
    const onDataInvalidated = (ev: Event) => {
      const detail = (ev as CustomEvent<DataInvalidationDetail>).detail;
      const reason = String(detail?.reason ?? '');
      if (reason.includes('fallback-poll')) return;
      if (
        !shouldAcceptInvalidation(detail, {
          domain: ['expenses'],
          companyId,
          branchId: branchId === 'all' ? null : branchId ?? null,
        })
      ) {
        return;
      }
      if ((reason === 'created' || reason === 'updated') && detail?.entityId) {
        void patchExpenseInList(String(detail.entityId));
        return;
      }
      queue();
    };
    window.addEventListener(DATA_INVALIDATED_EVENT, onDataInvalidated as EventListener);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(DATA_INVALIDATED_EVENT, onDataInvalidated as EventListener);
    };
  }, [branchId, companyId, activated, loadExpenses, patchExpenseInList]);

  const notifyExpenseChanged = useCallback(
    (expenseId: string | null, reason: string) => {
      if (!companyId) return;
      dispatchExpenseLifecycleInvalidated({
        companyId,
        branchId: branchId === 'all' ? null : branchId ?? null,
        expenseId,
        reason,
      });
    },
    [companyId, branchId]
  );

  // Get expense by ID
  const getExpenseById = (id: string): Expense | undefined => {
    return expenses.find(e => e.id === id);
  };

  // Create new expense (options.branchId = override for drawer; options.payment_account_id = chart account id)
  const createExpense = async (
    expenseData: Omit<Expense, 'id' | 'expenseNo' | 'createdAt' | 'updatedAt'>,
    options?: { branchId?: string; payment_account_id?: string; paidToUserId?: string; expense_category_id?: string }
  ): Promise<Expense> => {
    const effectiveBranchId = options?.branchId ?? branchId;
    if (!companyId || !user) throw new Error('Company and user are required');
    if (!effectiveBranchId || effectiveBranchId === 'all') {
      if (requiresBranchSelection) throw new Error('Branch is required');
      throw new Error('No branch available. Please try again.');
    }

    try {
      const status =
        expenseData.status === 'paid'
          ? 'paid'
          : expenseData.status === 'approved'
            ? 'approved'
            : expenseData.status === 'rejected'
              ? 'rejected'
              : 'submitted';

      const result = await expenseService.createExpenseDocument({
        companyId,
        branchId: effectiveBranchId,
        createdBy: user.id,
        expense: {
          expense_date: expenseData.date,
          category: mapCategoryToSupabase(expenseData.category),
          description: expenseData.description,
          amount: expenseData.amount,
          payment_method: expenseData.paymentMethod,
          status,
          payment_account_id: options?.payment_account_id,
          receipt_url: expenseData.receiptUrl?.trim() || undefined,
        },
        patch: {
          paid_to_user_id: options?.paidToUserId,
          expense_category_id: options?.expense_category_id,
          vendor_name: expenseData.payeeName?.trim() || undefined,
        },
      });

      const expenseNo = String((result as { expense_no?: string }).expense_no || '');
      const receiptWarning = (result as { _receiptWarning?: string })._receiptWarning;
      const newExpense = convertFromSupabaseExpense(result);
      if (!newExpense.expenseNo && expenseNo) (newExpense as { expenseNo: string }).expenseNo = expenseNo;
      
      // Update local state
      setExpenses(prev => [newExpense, ...prev]);

      if (newExpense.status === 'paid' && options?.paidToUserId && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'user', entityId: options.paidToUserId } }));
      }

      toast.success(`Expense ${newExpense.expenseNo || expenseNo} created successfully!`);
      if (receiptWarning) toast.warning(receiptWarning);

      dispatchDataInvalidated({
        domain: 'expenses',
        companyId,
        branchId: effectiveBranchId,
        entityId: newExpense.id,
        reason: 'created',
      });

      if (newExpense.status === 'paid') {
        void expenseService.postExpenseAccounting(newExpense.id).then((post) => {
          if (!post.success) {
            toast.warning(
              post.error
                ? `Expense saved; accounting post failed: ${post.error}`
                : 'Expense saved; cash book may be missing until accounting posts.',
            );
          }
          dispatchAccountingInvalidated({
            companyId,
            branchId: effectiveBranchId,
            entityId: newExpense.id,
            reason: 'expense:created',
          });
        });
      }

      return newExpense;
    } catch (error: any) {
      console.error('[EXPENSE CONTEXT] Error creating expense:', error);
      toast.error(`Failed to create expense: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Update expense
  const updateExpense = async (
    id: string,
    updates: Partial<Expense> & { paymentAccountId?: string },
    options?: { silent?: boolean }
  ): Promise<void> => {
    const prev = expenseUpdateChainRef.current.get(id) ?? Promise.resolve();
    const next = prev.then(() => updateExpenseBody(id, updates, options));
    expenseUpdateChainRef.current.set(id, next);
    try {
      await next;
    } finally {
      if (expenseUpdateChainRef.current.get(id) === next) {
        expenseUpdateChainRef.current.delete(id);
      }
    }
  };

  const updateExpenseBody = async (
    id: string,
    updates: Partial<Expense> & { paymentAccountId?: string },
    options?: { silent?: boolean }
  ): Promise<void> => {
    const traceId = createExpenseEditTraceId(id);
    try {
      const existing = getExpenseById(id);
      if (!existing) throw new Error('Expense not found');
      pushExpenseEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        expenseId: id,
        companyId: companyId || null,
        phase: 'start',
        data: {
          oldSnapshot: {
            status: existing.status,
            amount: existing.amount,
            category: existing.category,
            paymentMethod: existing.paymentMethod,
            paymentAccountId: existing.paymentAccountId || null,
            date: existing.date,
            location: existing.location,
            description: existing.description,
            notes: existing.notes || null,
            payeeName: existing.payeeName || null,
          },
          updates,
        },
      });

      const supabaseUpdates: Record<string, unknown> = {};
      if (updates.category !== undefined) supabaseUpdates.category = mapCategoryToSupabase(updates.category);
      if (updates.status !== undefined) supabaseUpdates.status = updates.status;
      if (updates.amount !== undefined) supabaseUpdates.amount = updates.amount;
      if (updates.description !== undefined) supabaseUpdates.description = updates.description;
      if (updates.paymentMethod !== undefined) supabaseUpdates.payment_method = updates.paymentMethod;
      if (updates.payeeName !== undefined) supabaseUpdates.vendor_name = updates.payeeName;
      if (updates.notes !== undefined) supabaseUpdates.notes = updates.notes;
      if (updates.approvedBy !== undefined) supabaseUpdates.approved_by = updates.approvedBy;
      if (updates.approvedDate !== undefined) supabaseUpdates.approved_at = updates.approvedDate;
      if (updates.date !== undefined) supabaseUpdates.expense_date = updates.date;
      if (updates.location !== undefined) supabaseUpdates.branch_id = updates.location;
      if (updates.receiptUrl !== undefined) supabaseUpdates.receipt_url = updates.receiptUrl || null;
      const pAcc = updates.paymentAccountId;
      if (pAcc !== undefined) supabaseUpdates.payment_account_id = pAcc || null;

      const mergedPaymentMethod = updates.paymentMethod ?? existing.paymentMethod;
      const mergedAmount = updates.amount ?? existing.amount;
      const mergedCategory = updates.category ?? existing.category;
      const mergedDescription = updates.description ?? existing.description;
      const mergedDate = updates.date ?? existing.date;

      const existingSnap: PaidExpenseSnapshot = {
        status: existing.status,
        amount: Number(existing.amount) || 0,
        paymentMethod: existing.paymentMethod,
        date: existing.date,
        location: existing.location,
        paymentAccountId: existing.paymentAccountId,
        category: String(existing.category),
        description: existing.description || '',
        notes: existing.notes ?? null,
        payeeName: existing.payeeName ?? null,
      };

      const classification: ExpenseEditClassification = companyId
        ? classifyPaidExpenseEdit(existingSnap, updates, companyId)
        : {
            kind: 'NO_POSTING_CHANGE',
            reasons: ['no company context'],
            changedFields: [],
            postingChangedFields: [],
            headerOnlyChangedFields: [],
            nonPostingChangedFields: [],
            affectedDomains: [],
            domains: { header: false, accounting: false, inventory: false, payments: false },
            headerChangedFields: [],
            accountingChangedFields: [],
            inventoryChangedFields: [],
            actionPlan: {
              updateHeader: false,
              adjustAccounting: false,
              adjustInventory: false,
              touchPayments: false,
            },
            rollbackReversalOnRepostFailure: false,
          };
      assertDomainEditSafetyTestMode(classification, 'expense updateExpense');
      pushExpenseEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        expenseId: id,
        companyId: companyId || null,
        phase: 'classified',
        data: {
          classifier: classification.kind,
          affectedDomains: classification.affectedDomains,
          actionPlan: classification.actionPlan,
          reasons: classification.reasons,
          changedFields: classification.changedFields || [],
          postingChangedFields: classification.postingChangedFields || [],
          headerOnlyChangedFields: classification.headerOnlyChangedFields || [],
          nonPostingChangedFields: classification.nonPostingChangedFields || [],
          headerChangedFields: classification.headerChangedFields || [],
          accountingChangedFields: classification.accountingChangedFields || [],
          inventoryChangedFields: classification.inventoryChangedFields || [],
          canonicalComparison: buildExpenseCanonicalComparisonRows(existingSnap, updates),
        },
      });

      const hardBlockPosting = (op: 'reversal' | 'repost') => {
        if (classification.kind === 'FULL_REVERSE_REPOST' || classification.kind === 'DELTA_ADJUSTMENT') return;
        const stack = new Error(`[EXPENSE EDIT] blocked ${op} for ${classification.kind}`).stack;
        pushExpenseEditTrace({
          traceId,
          ts: getCurrentLocalTimestamp(),
          expenseId: id,
          companyId: companyId || null,
          phase: 'error',
          data: {
            blockedOperation: op,
            classifier: classification.kind,
            stack,
          },
        });
        throw new Error(`Blocked ${op}: classifier=${classification.kind}. This edit is non-financial.`);
      };

      const persistExpenseRow = async () => {
        if (Object.keys(supabaseUpdates).length > 0) {
          pushExpenseEditTrace({
            traceId,
            ts: getCurrentLocalTimestamp(),
            expenseId: id,
            companyId: companyId || null,
            phase: 'db_update',
            data: { supabaseUpdates },
          });
          await expenseService.updateExpense(id, supabaseUpdates as Partial<SupabaseExpense>);
        }
      };

      const logHeaderOnlyActivity = async () => {
        if (!companyId || !user?.id) return;
        const rows: Parameters<typeof formatFieldChangeLines>[0] = [];
        if (updates.date !== undefined && updates.date !== existing.date) {
          rows.push({
            field: 'expense_date',
            label: 'Expense date',
            oldValue: existing.date,
            newValue: updates.date,
          });
        }
        if (
          updates.notes !== undefined &&
          normalizeNullableText(updates.notes) !== normalizeNullableText(existing.notes)
        ) {
          rows.push({
            field: 'notes',
            label: 'Notes',
            oldValue: existing.notes ?? '',
            newValue: updates.notes ?? '',
          });
        }
        if (
          updates.description !== undefined &&
          String(updates.description ?? '').trim() !== String(existing.description ?? '').trim()
        ) {
          rows.push({
            field: 'description',
            label: 'Description',
            oldValue: existing.description,
            newValue: updates.description,
          });
        }
        if (
          updates.payeeName !== undefined &&
          normalizeNullableText(updates.payeeName) !== normalizeNullableText(existing.payeeName)
        ) {
          rows.push({
            field: 'payee',
            label: 'Payee',
            oldValue: existing.payeeName ?? '',
            newValue: updates.payeeName ?? '',
          });
        }
        const lines = formatFieldChangeLines(rows);
        if (lines.length === 0) return;
        await logDocumentEditActivity({
          companyId,
          module: 'expense',
          entityId: id,
          entityReference: existing.expenseNo,
          action: 'expense_header_edited',
          lines,
          performedBy: user.id,
        });
      };

      if (classification.kind === 'BLOCKED_CLOSED_PERIOD') {
        throw new Error('This expense date falls in a locked accounting period. Change the date or ask an admin.');
      }

      if (classification.kind === 'NO_POSTING_CHANGE') {
        await persistExpenseRow();
        await logHeaderOnlyActivity();
      } else if (classification.kind === 'HEADER_ONLY_CHANGE') {
        await persistExpenseRow();
        await logHeaderOnlyActivity();
        const presentation = classification.presentation || {};
        const patch = await accountingService.patchExpensePostingPresentation({
          companyId: companyId!,
          expenseId: id,
          entryDate: presentation.entryDate,
          journalDescription: presentation.journalDescription,
        });
        if (!patch.ok) {
          throw new Error(patch.error || 'Could not update expense journal header (no reversal was posted).');
        }
        pushExpenseEditTrace({
          traceId,
          ts: getCurrentLocalTimestamp(),
          expenseId: id,
          companyId: companyId || null,
          phase: 'header_patch',
          data: {
            entryDate: presentation.entryDate || null,
            journalDescription: presentation.journalDescription || null,
          },
        });
      } else if (
        (classification.kind === 'FULL_REVERSE_REPOST' || classification.kind === 'DELTA_ADJUSTMENT') &&
        companyId
      ) {
        // In-place update: modify existing JE lines directly instead of reverse + repost
        const { data: jeRow } = await supabase
          .from('journal_entries')
          .select('id, description')
          .eq('company_id', companyId)
          .eq('reference_type', 'expense')
          .eq('reference_id', id)
          .or('is_void.is.null,is_void.eq.false')
          .not('reference_type', 'eq', 'correction_reversal')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        const jeId = (jeRow as { id?: string; description?: string } | null)?.id;
        if (jeId) {
          const newAmount = Number(mergedAmount) || 0;

          // Resolve correct GL expense account based on category
          const catLower = String(mergedCategory || '').toLowerCase();
          const catAccountMap: Record<string, string> = {
            salaries: '6110', salary: '6110', wages: '6110',
            marketing: '6120', advertising: '6120',
            rent: '6100', utilities: '6100', office: '6100',
            shipping: '5100', freight: '5100', courier: '5100',
            production: '5000', manufacturing: '5000',
          };
          const targetCode = catAccountMap[catLower] || '6100';
          const { data: targetAcct } = await supabase.from('accounts').select('id').eq('code', targetCode).eq('company_id', companyId).eq('is_active', true).maybeSingle();

          // Update debit lines: amount + correct account
          const debitUpdate: Record<string, any> = { debit: newAmount };
          if (targetAcct?.id) debitUpdate.account_id = targetAcct.id;
          await supabase
            .from('journal_entry_lines')
            .update(debitUpdate)
            .eq('journal_entry_id', jeId)
            .gt('debit', 0);
          // Update credit lines: amount only
          await supabase
            .from('journal_entry_lines')
            .update({ credit: newAmount })
            .eq('journal_entry_id', jeId)
            .gt('credit', 0);
          // Update description and log edit
          const ts = new Date().toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' });
          const oldDesc = (jeRow as any)?.description || '';
          const newDesc = mergedDescription || String(mergedCategory) || oldDesc;
          const editNote = `[Edited ${ts}: Rs ${Number(existing?.amount || 0).toLocaleString()} → Rs ${newAmount.toLocaleString()}]`;
          await supabase.from('journal_entries').update({
            description: `${newDesc} ${editNote}`.slice(0, 500),
            entry_date: mergedDate || undefined,
          }).eq('id', jeId);

          pushExpenseEditTrace({
            traceId,
            ts: getCurrentLocalTimestamp(),
            expenseId: id,
            companyId: companyId || null,
            phase: 'inplace_update',
            data: { jeId, oldAmount: existing?.amount, newAmount, description: newDesc },
          });

          const mergedPayAcc =
            updates.paymentAccountId !== undefined
              ? updates.paymentAccountId || null
              : existing.paymentAccountId || null;

          if (classification.actionPlan.touchPayments) {
            const sync = await syncExpenseLinkedPayment({
              companyId,
              expenseId: id,
              expenseNo: existing.expenseNo,
              amount: newAmount,
              paymentAccountId: mergedPayAcc,
              jeId,
              performedBy: user?.id ?? null,
            });
            if (!sync.ok) {
              throw new Error(sync.error || 'Could not sync linked payment amount to expense.');
            }
            pushExpenseEditTrace({
              traceId,
              ts: getCurrentLocalTimestamp(),
              expenseId: id,
              companyId: companyId || null,
              phase: 'payment_sync',
              data: { paymentId: sync.paymentId, updated: sync.updated },
            });
          }

          // Refresh
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('accountingEntriesChanged'));
          }

        }
        await persistExpenseRow();
      } else {
        await persistExpenseRow();
      }

      setExpenses((prev) =>
        prev.map((expense) => {
          if (expense.id !== id) return expense;
          return {
            ...expense,
            ...updates,
            paymentAccountId:
              updates.paymentAccountId !== undefined ? updates.paymentAccountId : expense.paymentAccountId,
            category:
              updates.category !== undefined
                ? mapCategoryFromSupabase(mapCategoryToSupabase(updates.category))
                : expense.category,
            payeeName:
              updates.payeeName !== undefined ? updates.payeeName || '' : expense.payeeName,
            notes: updates.notes !== undefined ? updates.notes : expense.notes,
            description:
              updates.description !== undefined ? updates.description : expense.description,
            date: updates.date !== undefined ? updates.date : expense.date,
            amount: updates.amount !== undefined ? updates.amount : expense.amount,
            paymentMethod:
              updates.paymentMethod !== undefined ? updates.paymentMethod : expense.paymentMethod,
            location: updates.location !== undefined ? updates.location : expense.location,
            status: updates.status !== undefined ? updates.status : expense.status,
            updatedAt: getCurrentLocalTimestamp(),
          };
        })
      );

      if (!options?.silent) toast.success('Expense updated successfully!');
      notifyExpenseChanged(id, 'updated');
      void accounting?.refreshEntries();
      pushExpenseEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        expenseId: id,
        companyId: companyId || null,
        phase: 'done',
        data: { classifier: classification.kind },
      });
    } catch (error: any) {
      console.error('[EXPENSE CONTEXT] Error updating expense:', error);
      pushExpenseEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        expenseId: id,
        companyId: companyId || null,
        phase: 'error',
        data: { message: error?.message || String(error) },
      });
      toast.error(`Failed to update expense: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Delete expense (draft/unposted only)
  const deleteExpense = async (id: string): Promise<void> => {
    const expense = getExpenseById(id);
    if (!expense) {
      throw new Error('Expense not found');
    }
    if (isPostedExpenseStatus(expense.status)) {
      throw new Error('Posted expenses must be cancelled, not deleted.');
    }

    try {
      await expenseService.deleteExpense(id, companyId || undefined);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      toast.success(`${expense.expenseNo} deleted successfully!`);
      notifyExpenseChanged(id, 'deleted');
    } catch (error: any) {
      console.error('[EXPENSE CONTEXT] Error deleting expense:', error);
      toast.error(`Failed to delete expense: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  const cancelExpense = async (id: string, reason = 'Cancelled by user'): Promise<void> => {
    const expense = getExpenseById(id);
    if (!expense) {
      throw new Error('Expense not found');
    }
    if (!companyId) {
      throw new Error('Company not loaded');
    }

    try {
      await expenseService.cancelPostedExpense(id, companyId, reason, user?.id ?? null);
      setExpenses((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, status: 'rejected' as ExpenseStatus, updatedAt: new Date().toISOString() } : e
        )
      );
      void accounting?.refreshEntries();
      toast.success(`${expense.expenseNo} cancelled — audit trail preserved.`);
      notifyExpenseChanged(id, 'cancelled');
    } catch (error: any) {
      console.error('[EXPENSE CONTEXT] Error cancelling expense:', error);
      toast.error(`Failed to cancel expense: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Approve expense
  const approveExpense = async (id: string, approvedBy: string): Promise<void> => {
    const expense = getExpenseById(id);
    if (!expense) {
      throw new Error('Expense not found');
    }

    try {
      const now = getCurrentLocalTimestamp();
      
      await updateExpense(id, {
        status: 'approved',
        approvedBy,
        approvedDate: now,
      });

      toast.success(`${expense.expenseNo} approved successfully!`);
    } catch (error) {
      throw error;
    }
  };

  // Reject expense
  const rejectExpense = async (id: string): Promise<void> => {
    const expense = getExpenseById(id);
    if (!expense) {
      throw new Error('Expense not found');
    }

    try {
      await updateExpense(id, { status: 'rejected' });
      toast.success(`${expense.expenseNo} rejected!`);
    } catch (error) {
      throw error;
    }
  };

  // Mark as paid
  const markAsPaid = async (id: string, paymentMethod: string): Promise<void> => {
    const expense = getExpenseById(id);
    if (!expense) {
      throw new Error('Expense not found');
    }

    try {
      await updateExpense(
        id,
        {
          status: 'paid',
          paymentMethod,
        },
        { silent: true }
      );

      const post = await expenseService.postExpenseAccounting(expense.id);

      if (!post.success) {
        toast.error(
          post.error
            ? `Expense marked paid but accounting post failed: ${post.error}`
            : 'Expense marked paid but accounting post failed. Check Chart of Accounts.',
        );
      } else {
        toast.success(`${expense.expenseNo} marked as paid and posted to accounting!`);
      }
      void accounting?.refreshEntries();
    } catch (error) {
      throw error;
    }
  };

  // Get expenses by category
  const getExpensesByCategory = (category: ExpenseCategory): Expense[] => {
    return expenses.filter(e => e.category === category);
  };

  // Get expenses by status
  const getExpensesByStatus = (status: ExpenseStatus): Expense[] => {
    return expenses.filter(e => e.status === status);
  };

  // Get total by category
  const getTotalByCategory = (category: ExpenseCategory): number => {
    return expenses
      .filter(e => e.category === category && e.status === 'paid')
      .reduce((sum, e) => sum + e.amount, 0);
  };

  const value = useMemo<ExpenseContextType>(() => ({
    expenses,
    loading,
    getExpenseById,
    createExpense,
    updateExpense,
    deleteExpense,
    cancelExpense,
    approveExpense,
    rejectExpense,
    markAsPaid,
    getExpensesByCategory,
    getExpensesByStatus,
    getTotalByCategory,
    refreshExpenses: loadExpenses,
    __activate: activate,
  }), [
    expenses, loading, getExpenseById, createExpense, updateExpense, deleteExpense, cancelExpense,
    approveExpense, rejectExpense, markAsPaid, getExpensesByCategory,
    getExpensesByStatus, getTotalByCategory, loadExpenses, activate,
  ]);

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};
